import { useState, type ReactNode } from "react";

import { Tile } from "./Tile";
import { TileGrid } from "./TileGrid";
import { EyeIcon, EyeOffIcon } from "./icons";

export interface TileBoardItem {
  key: string;
  title: ReactNode;
  content: ReactNode;
  /** Pinned tiles cannot be hidden (e.g. the always-visible "Basis" tile). They can still move. */
  pinned?: boolean;
  /**
   * Maakt het sleepgreepje (de puntjes rechtsboven, alleen in de bewerkstand) ook een knop: klikken opent
   * bijv. de instellingen van de tegel, slepen verplaatst hem nog steeds. Optioneel; zonder is het greepje
   * zoals altijd alleen een sleepgreep.
   */
  onHandleClick?: () => void;
  /** Tooltip/aria-label van het klikbare greepje. */
  handleLabel?: string;
}

export interface TileBoardLabels {
  /** aria/title for the hide control. */
  hide: string;
  /** aria/title for the drag handle. */
  drag: string;
  /** Heading above the hidden-tiles tray. */
  hiddenSection: string;
}

export interface TileBoardProps {
  items: TileBoardItem[];
  /** Ordered tile keys. Keys not present in {@link items} are ignored. */
  order: string[];
  /** Keys that are currently hidden. */
  hidden: string[];
  /** When true, tiles expose a hide control + drag handle and the hidden-tiles tray is shown. */
  editing: boolean;
  onReorder: (order: string[]) => void;
  onToggleHidden: (key: string, hidden: boolean) => void;
  /**
   * Of elke tegel zelf een verbergknop krijgt en verborgen tegels in een lade onderaan komen.
   * Standaard aan. Zet dit uit als het scherm het verbergen ergens anders regelt — het dierdetail
   * doet dat via één keuzedialoog achter het oog naast het slotje, zoals de bron.
   */
  showHideControls?: boolean;
  labels: TileBoardLabels;
}

/** Move `dragKey` to sit immediately before `targetKey` in `order`. Pure — exported for testing. */
export function moveBefore(order: string[], dragKey: string, targetKey: string): string[] {
  if (dragKey === targetKey) return order;
  const next = order.filter((k) => k !== dragKey);
  const idx = next.indexOf(targetKey);
  if (idx === -1) return order;
  next.splice(idx, 0, dragKey);
  return next;
}

/**
 * A re-orderable, show/hide-able grid of {@link Tile}s — the `fui-tile-board` vocabulary. Controlled:
 * the parent owns `order`, `hidden` and `editing` (and their persistence) and reacts to `onReorder` /
 * `onToggleHidden`. Re-ordering uses native HTML5 drag-and-drop and is only active while `editing`.
 * Hidden tiles surface in a tray with a restore button. Locale-free: all control text comes via
 * `labels`.
 */
export function TileBoard({
  items,
  order,
  hidden,
  editing,
  onReorder,
  onToggleHidden,
  showHideControls = true,
  labels,
}: TileBoardProps) {
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const byKey = new Map(items.map((i) => [i.key, i]));
  const hiddenSet = new Set(hidden);
  const visibleKeys = order.filter((k) => byKey.has(k) && !hiddenSet.has(k));
  const hiddenItems = order.filter((k) => byKey.has(k) && hiddenSet.has(k)).map((k) => byKey.get(k)!);

  const endDrag = () => {
    setDragKey(null);
    setOverKey(null);
  };

  const titleText = (item: TileBoardItem) => (typeof item.title === "string" ? item.title : item.key);

  return (
    <div className="fui-tile-board">
      <TileGrid>
        {visibleKeys.map((key) => {
          const item = byKey.get(key)!;
          const actions = editing ? (
            <>
              {showHideControls && !item.pinned && (
                <button
                  type="button"
                  className="fui-tile__icon-btn"
                  aria-label={`${labels.hide} — ${titleText(item)}`}
                  title={labels.hide}
                  onClick={() => onToggleHidden(key, true)}
                >
                  <EyeOffIcon width={16} height={16} />
                </button>
              )}
              {item.onHandleClick ? (
                <button
                  type="button"
                  className="fui-tile__drag fui-tile__drag--knop"
                  title={item.handleLabel ?? labels.drag}
                  aria-label={`${item.handleLabel ?? labels.drag} — ${titleText(item)}`}
                  onClick={item.onHandleClick}
                >
                  ⠿
                </button>
              ) : (
                <span className="fui-tile__drag" title={labels.drag} aria-label={labels.drag} role="img">
                  ⠿
                </span>
              )}
            </>
          ) : undefined;

          return (
            <Tile
              key={key}
              title={item.title}
              actions={actions}
              editing={editing}
              draggable={editing}
              dragging={dragKey === key}
              dropTarget={editing && overKey === key && dragKey !== null && dragKey !== key}
              onDragStart={(e) => {
                setDragKey(key);
                if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => {
                if (editing && dragKey) {
                  e.preventDefault();
                  setOverKey(key);
                }
              }}
              onDragLeave={() => {
                setOverKey((prev) => (prev === key ? null : prev));
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragKey) onReorder(moveBefore(order, dragKey, key));
                endDrag();
              }}
              onDragEnd={endDrag}
            >
              {item.content}
            </Tile>
          );
        })}
      </TileGrid>

      {showHideControls && editing && hiddenItems.length > 0 && (
        <div className="fui-tile-board__hidden">
          <span className="fui-tile-board__hidden-title">{labels.hiddenSection}</span>
          <div className="fui-tile-board__hidden-list">
            {hiddenItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className="fui-tile-board__restore"
                onClick={() => onToggleHidden(item.key, false)}
              >
                <EyeIcon width={16} height={16} />
                <span>{item.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
